import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  ChartDropdownCustomerAvailableDimensions,
  ChartDropdownDimensionEnum,
  CubeDimensionEnum,
  FEATURE_INFO_DICTIONARY,
  FeatureEnum,
  featureValueOptions,
  setCurrChartDimension,
} from '../../services/data/impl/cube/cubeSlice';
import { isBlueberryFeature } from '../../services/data/impl/cube/cubeUtils';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import SVGViewer from '../commonUI/SVGViewer';
import useCurrServicesAndMeasures from '../customerSummary/hooks/useCurrServicesAndMeasures';
import { selectCurrFeature, setCurrFeature } from '../select/selectSlice';
import hashtagIcon from './icons/hashtag.svg';
import rulerIcon from './icons/ruler.svg';
interface Props {
  type: 'MapFeature' | 'ChartFeature' | 'ChartDimension';
  value: FeatureEnum | ChartDropdownDimensionEnum;
  items: FeatureEnum[] | ChartDropdownDimensionEnum[] | null;
  colorScheme?: 'light' | 'dark';
  fullWidth?: boolean;
}

const filterDropdown = ({
  item,
  currFeature,
  type,
  disableFilters,
}: {
  item: FeatureEnum | ChartDropdownDimensionEnum;
  currFeature: FeatureEnum;
  type: Props['type'];
  disableFilters: boolean;
}) => {
  if (type === 'ChartDimension' && !disableFilters) {
    const chartConditions = [
      !ChartDropdownCustomerAvailableDimensions.find(
        (available) => available === (item as ChartDropdownDimensionEnum)
      ),
      [FeatureEnum.shoot, FeatureEnum.earlyShoot].includes(currFeature) &&
        item === ChartDropdownDimensionEnum.colorDistribution,
      item === ChartDropdownDimensionEnum.fruitSize &&
        !isBlueberryFeature(currFeature),
    ];

    if (chartConditions.some((condition) => condition)) {
      return false;
    }
  }
  return true;
};

const CubeDropdown: React.FC<Props> = ({
  type,
  items,
  value,
  colorScheme = 'dark',
  fullWidth = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currFeature = useAppSelector(selectCurrFeature);
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();
  const { Diagnostics } = useFeatureFlags();
  const { t } = useTranslation();

  const handleChange = (event: SelectChangeEvent) => {
    const newVal = event.target.value;
    if (type === 'MapFeature') {
      if (Object.values(FeatureEnum).includes(newVal as FeatureEnum)) {
        dispatch(setCurrFeature(newVal as FeatureEnum));
      }
    } else if (type === 'ChartDimension') {
      if (
        Object.values(ChartDropdownDimensionEnum).includes(
          newVal as ChartDropdownDimensionEnum
        )
      ) {
        dispatch(setCurrChartDimension(newVal as ChartDropdownDimensionEnum));
      }
    }
  };

  const menuItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const filteredItems = (items as []).filter((el) =>
      filterDropdown({
        item: el,
        currFeature,
        type,
        disableFilters: !Diagnostics,
      })
    ) as FeatureEnum[] | ChartDropdownDimensionEnum[];

    return filteredItems.map((item) => {
      let menuItemName = item[0].toUpperCase() + item.slice(1);
      // if chart feature or map feature, disable if not in the list of models that ran (AIModelsRunOnScan) queried from cube
      let disabled = false;
      if (
        (type === 'ChartFeature' || type === 'MapFeature') &&
        typeof item === 'string' &&
        (featureValueOptions as string[]).includes(item)
      ) {
        menuItemName =
          FEATURE_INFO_DICTIONARY[item as FeatureEnum][0].dropdownLabel; // get the label from the dictionary

        const modelNames =
          servicesAndMeasuresWithFeatureFilter.availableServicesRaw.map(
            (aiClass) => aiClass[CubeDimensionEnum.modelName] as string
          );
        disabled = !modelNames.includes(item);
      }

      return (
        <MenuItem key={item} value={item} disabled={disabled}>
          {t(menuItemName)}
        </MenuItem>
      );
    });
  }, [
    items,
    type,
    currFeature,
    servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
    t,
    Diagnostics,
  ]);

  const iconSize = 20;

  const icon = (
    <div
      style={{
        marginRight: '0.5rem',
        height: `${iconSize}px`,
        width: 'min-content',
        marginBottom: '0.2rem',
      }}
    >
      <SVGViewer
        svg={type === 'ChartDimension' ? rulerIcon : hashtagIcon}
        alt='select icon'
        pxSize={iconSize}
        hexColor={
          colorScheme === 'dark'
            ? theme.colors.common.softWhite
            : theme.colors.common.softBlack
        }
      />
    </div>
  );

  return (
    <motion.div
      layout
      transition={{ duration: 0 }}
      style={{
        width: fullWidth ? '100%' : 'min-content',
        height: '2.2rem',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        borderRadius: theme.borderRadius.primary,
        padding: '0.3rem 0 0.3rem 0.5rem',
        backgroundColor:
          colorScheme === 'dark'
            ? theme.colors.darkTheme.grey.medium
            : theme.colors.common.softWhite,
        boxShadow: 'none',
      }}
    >
      {icon}
      <FormControl sx={{ width: '100%' }}>
        <Select
          value={menuItems.length ? value : ''}
          label={type}
          onChange={handleChange}
          displayEmpty
          IconComponent={ExpandMoreIcon}
          sx={{
            width: '100%',
            fontSize: '1rem',
            color:
              colorScheme === 'dark'
                ? theme.colors.common.white
                : theme.colors.common.softBlack,
            '& .MuiSelect-select': { p: 0 },
            '& .MuiSelect-icon': {
              color:
                colorScheme === 'dark'
                  ? theme.colors.common.white
                  : theme.colors.common.softBlack,
            },
          }}
        >
          {menuItems}
        </Select>
      </FormControl>
    </motion.div>
  );
};

export default CubeDropdown;
